import { BrandMediaFile, LogoVariant } from 'types'
import { BrandMediaType } from 'types'
import { MissingLogo } from './MissingLogo'
import { DeleteButton } from './DeleteButton'
import { ReplaceButton } from './ReplaceButton'
import cx from 'classnames'
import Styles from './LogoVariants.module.scss'
import { useCreateBrandMediaFile } from 'hooks/queries/workspaces/brandVisualKits/useCreateBrandMediaFile'
import { useDeleteBrandMediaFile } from 'hooks/queries/workspaces/brandVisualKits/useDeleteBrandMediaFile'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useEffect, useState } from 'react'
import { toBase64 } from 'flows/common/steps/BrandKitColorsAndFonts/utils'
import { useRegenerateLogoVariant } from 'hooks/queries/useRegenerateLogoVariant'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface LogoProps {
  brandVisualKitId: number
  variant: LogoVariant
  logos: BrandMediaFile[]
  onLogoChange?: () => void
  onUploadingChange?: (isUploading: boolean) => void
  className?: string
  editTrigger?: React.ReactNode
  showDelete?: boolean
  showActions?: boolean
  actionPlacement?: 'left' | 'right' | 'space-between'
}

export const Logo = ({
  brandVisualKitId,
  logos,
  variant,
  onLogoChange,
  onUploadingChange,
  className,
  editTrigger,
  showDelete = true,
  showActions = false,
  actionPlacement = 'space-between',
}: LogoProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const logo = logos.filter((l) => l.logo_variant === variant).sort((a, b) => b.id - a.id)[0]
  const originalLogo = logos.find((logo) => logo.logo_variant === LogoVariant.Original)

  const [tempUrl, setTempUrl] = useState<string>()
  const [newLogo, setNewLogo] = useState<BrandMediaFile>()
  const { uploadImageFiles, isUploading } = useImportMediaFiles(false)
  const { mutateAsync: deleteBrandMediaFile, isLoading: isDeletingLogo } = useDeleteBrandMediaFile()
  const { mutateAsync: createBrandMediaFile, isLoading: isCreatingLogo } = useCreateBrandMediaFile()
  const { mutateAsync: regenerateLogoVariant, isLoading: isRegeneratingLogoVariant } = useRegenerateLogoVariant({
    brandVisualKitId,
  })
  const canRegenerate = originalLogo && variant !== LogoVariant.Original

  const isDisabled = isDeletingLogo || isCreatingLogo || isUploading

  useEffect(() => {
    onUploadingChange?.(isUploading || isCreatingLogo)
    return () => onUploadingChange?.(false)
  }, [isUploading, isCreatingLogo, onUploadingChange])

  const handleDelete = async (logo: BrandMediaFile) => {
    if (isDeletingLogo) {
      return
    }

    setTempUrl(undefined)
    await deleteBrandMediaFile({ id: logo.id, brandVisualKitId }, { onSettled: () => onLogoChange?.() })
  }

  const handleReplace = async (variant: LogoVariant, file: File) => {
    if (isCreatingLogo) {
      return
    }

    const base64Url = (await toBase64(file)) as string
    setTempUrl(base64Url)

    const almanacFiles = await uploadImageFiles(Array.from([file]))

    if (!almanacFiles || almanacFiles.length === 0) {
      setTempUrl(undefined)
      return
    }

    const currentLogo = logos.find((logo) => logo.logo_variant === variant)
    await createBrandMediaFile(
      {
        brandMediaType: BrandMediaType.LOGO,
        brandVisualKitId,
        mediaFileId: almanacFiles[0].media_file_id as number,
        logoVariant: variant,
        isPrimaryLogo: true,
      },
      {
        onSettled: (data) => {
          setNewLogo(data?.brand_media_file)
          if (currentLogo) {
            handleDelete(currentLogo)
          }
        },
      },
    )
    if (!currentLogo) onLogoChange?.()
  }

  const handleRegenerate = async () => {
    if (!originalLogo || variant === LogoVariant.Original) {
      return
    }
    await regenerateLogoVariant({ brandVisualKitId, variant, originalLogo })
    onLogoChange?.()
  }

  const logoUrl = tempUrl || newLogo?.media_file.source_url || logo?.media_file.source_url

  if (!logoUrl) {
    return (
      <div className={cx(Styles.logo, className, Styles.missingLogo)}>
        <MissingLogo
          onUpload={(file) => handleReplace(variant, file)}
          isUploading={isUploading || isCreatingLogo || isRegeneratingLogoVariant}
          onRegenerate={canRegenerate ? () => handleRegenerate() : undefined}
        />
        {!isSystemRedesignEnabled && <span className={Styles.chip}>{variant}</span>}
      </div>
    )
  }

  if (!logo) {
    return (
      <div
        className={cx(Styles.logo, className, { [Styles.light]: variant === LogoVariant.Light })}
        data-testid={`logo-${variant}`}
      >
        <img src={logoUrl} alt="" />
        {!isSystemRedesignEnabled && <span className={Styles.chip}>{variant}</span>}
      </div>
    )
  }

  return (
    <div
      className={cx(Styles.logo, className, { [Styles.light]: variant === LogoVariant.Light })}
      data-testid={`logo-${variant}`}
    >
      <div
        className={cx(Styles.logoActions, Styles[actionPlacement], {
          [Styles.showActions]: showActions,
        })}
      >
        <ReplaceButton
          onReplace={(file) => handleReplace(variant, file)}
          isDisabled={isDisabled}
          editTrigger={editTrigger}
        />
        {showDelete && (
          <DeleteButton
            onDelete={async () => {
              await handleDelete(logo)
              setNewLogo(undefined)
            }}
            isDisabled={isDisabled}
          />
        )}
      </div>
      <img src={logoUrl} alt="" />
      {!isSystemRedesignEnabled && <span className={Styles.chip}>{variant}</span>}
    </div>
  )
}
